import React, {useCallback, useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import {useTheme} from "@emotion/react";
import {BsFillCaretLeftFill, BsFillCaretRightFill} from "react-icons/bs";
import ReactPlayer from "react-player";
import {Link, useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {useModelContext} from "../../../context/ModelContextProvider";
import {
    BROADCAST_ON_SUBJECT_END_WITH_AUTO_PLAY,
    BROADCAST_ON_SUBJECT_IS_VIEWED_UPDATE,
    BROADCAST_ON_SUBJECT_SELECT,
    useApplicationContext,
} from "../../../context/ApplicationContextProvider";
import {useMobileDetect, useParameters} from "../../../utils/Hooks";
import Log from "../../../common/Log";
import {dialog_type_custom, useDialogContext} from "../../../context/DialogContextProvider";
import CourseClearModal from "../../_common/component/CourseClearModal";
import {lang} from "../../../lang";

/*
    props
    active_subject : {}             현재 재생될 subject 객체
    subject_set : [{}, {}, ...]     한 course 의 모든 subject 배열
    is_user_disabled : bool         강의를 듣는 도중 계정이 비활성화 되면 true
 */
const Player = (props) => {
    const {broadcast} = useApplicationContext();
    const {params} = useParameters();
    const {course_model, isSuccess, isError} = useModelContext();
    const {showDialog, alert} = useDialogContext();
    const navigate = useNavigate();
    const temporary_reducer = useSelector((state) => state.temporary_reducer);
    const {is_mobile} = useMobileDetect();

    // state
    const [is_buffer, setIsBuffer] = useState(false);
    const [is_play, setIsPlay] = useState(false);
    const [is_error, setIsError] = useState(false);
    const [auto_play, setAutoPlay] = useState(false);
    const [subject_vimeo_id, setSubjectVimeoId] = useState(null);
    const [can_render, setCanRender] = useState(false);

    // DOM ref
    const player = useRef();

    // ref
    const time_out = useRef();
    const time_set = useRef();
    const study_duration = useRef(0);
    const course_id = useRef(params.course_id);

    // css style
    const {view} = useComponentStyle(is_mobile);

    // 총 수강한 시간 업데이트
    const setSubjectViewedDuration = useCallback(async () => {
        if (study_duration.current === 0) {
            return false;
        } else {
            const subject_viewed_duration_res = await course_model.putSubjectViewedDuration(
                course_id.current,
                props.active_subject.id,
                study_duration.current
            );

            if (isSuccess(subject_viewed_duration_res)) {
                return true;
            } else if (isError(subject_viewed_duration_res)) {
                Log.private("putSubjectViewedDuration 에러", subject_viewed_duration_res);
                return false;
            } else {
                // 401, RESPONSE_INTERRUPTED (비활성, 삭제된 계정)
                return false;
            }
        }
    }, [course_model, isError, isSuccess, props.active_subject.id]);

    useEffect(() => {
        if (temporary_reducer.auto_play_check && !props.is_user_disabled.current) {
            setAutoPlay(temporary_reducer.auto_play_check);
        }

        // 페이지에서 벗어날 때 공부한 시간만큼 post
        return () => {
            clearInterval(time_set.current);
            if (!props.is_user_disabled.current) {
                setSubjectViewedDuration();
            }
        };
    }, [props.is_user_disabled, temporary_reducer, setSubjectViewedDuration]);

    useEffect(() => {
        study_duration.current = 0;
        clearTimeout(time_set.current);

        if (props.active_subject.vimeo_id) {
            setSubjectVimeoId(props.active_subject.vimeo_id);
            setCanRender(true);
        }
    }, [props.active_subject]);

    const onBuffered = () => {
        clearInterval(time_set.current);
        time_out.current = setTimeout(() => {
            setIsBuffer(true);
        }, 3000);
    };

    const onBufferedEnd = () => {
        clearTimeout(time_out.current);
        setIsBuffer(false);
    };

    const onSecChange = (change_type = "next", e) => {
        let duration = 0;
        let current_time = 0;
        let change_time;

        if (player.current) {
            duration = player.current.getDuration();
            current_time = player.current.getCurrentTime();
        }

        if (change_type === "next") {
            if (duration - current_time < 10) {
                change_time = duration - 10;
            } else {
                change_time = current_time + 10;
            }
        } else {
            if (current_time < 10) {
                change_time = 0;
            } else {
                change_time = current_time - 10;
            }
        }

        player.current.seekTo(change_time);
    };

    const onPlay = () => {
        setIsError(false);
        setIsBuffer(false);
        if (!time_set.current) {
            time_set.current = setInterval(() => {
                study_duration.current += 1;
            }, 1000);
        } else {
            clearInterval(time_set.current);
            time_set.current = setInterval(() => {
                study_duration.current += 1;
            }, 1000);
        }
    };

    const onPause = () => {
        clearInterval(time_set.current);
    };

    const onEnded = async () => {
        /*
            @TODO : 영상이 끝나면 수강체크
              - 시간을 고려하지 않았기 때문에 악의적으로 영상을 끝부분만 플레이한 뒤 수강체크 할 우려
         */
        if (!is_play || is_error) {
            return false;
        } else {
            clearInterval(time_set.current);

            // 강의의 수강기간이 만료되면 is_viewed체크, 다음 강의로 전달 X
            if (await isCourseExpired()) {
                // 현재 강의가 끝나면 SubjectList의 현재 강의 is_viewed 업데이트 (체크 아이콘)
                broadcast.send(BROADCAST_ON_SUBJECT_IS_VIEWED_UPDATE, props.active_subject);

                const subject_is_viewed_res = await course_model.postSubjectIsViewed(
                    course_id.current,
                    props.active_subject.id
                );
                if (isSuccess(subject_is_viewed_res)) {
                    // is_viewed가 업데이트 되면 수강한 시간 업데이트
                    if (await setSubjectViewedDuration()) {
                        await passNextSubject();
                    }
                } else if (isError(subject_is_viewed_res)) {
                    Log.private("putSubjectIsViewed 에러", subject_is_viewed_res);
                    await alert(lang.request.request_error_course_expired);
                    navigate("/");
                } else {
                    // 401, RESPONSE_INTERRUPTED (비활성, 삭제된 계정)
                    props.is_user_disabled.current = true;
                }
            }
        }
    };

    // 연속 재생 중이면 다음 강의로 넘기기
    const passNextSubject = async () => {
        // 다음 강의로 이동
        let next_subject_index = props.subject_set.findIndex((subject) => subject.id === props.active_subject.id) + 1;

        // 마지막 강의였다면 모든 강의 중 수강하지 않은 강의로 이동
        if (next_subject_index >= props.subject_set.length) {
            next_subject_index = props.subject_set.findIndex((subject) => !subject.is_viewed);
            if (next_subject_index === -1) {
                // 마지막 강의 재생 후 모든 강의를 수강했다면 축하 alert 띄우기
                showDialog({
                    type: dialog_type_custom,
                    component: CourseClearModal,
                    with_history: false,
                });
                next_subject_index = 0;
            }
        }
        // 수강체크 후 연속 재생이면 다음 강의로 이동 및 SubjectList 업데이트
        if (auto_play) {
            broadcast.send(BROADCAST_ON_SUBJECT_SELECT, props.subject_set[next_subject_index]);
            broadcast.send(BROADCAST_ON_SUBJECT_END_WITH_AUTO_PLAY, props.subject_set[next_subject_index]);
        }
    };

    // 강의를 듣는 도중 수강 기간이 만료된 경우
    const isCourseExpired = async () => {
        if (new Date() >= new Date(props.active_subject.rental_expire_time)) {
            setAutoPlay(false);
            await alert(lang.request.request_error_course_expired);
            navigate("/course-list");
            return false;
        } else {
            return true;
        }
    };

    const onError = () => {
        setIsError(true);
        clearTimeout(time_set.current);
    };

    return (
        <>
            {!is_buffer && is_play && !is_mobile ? (
                <>
                    <view.MoveButtonWrapper
                        move={"prev"}
                        onMouseEnter={(e) => {
                            e.currentTarget.classList.add("hover");
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.classList.remove("hover");
                        }}>
                        <view.MoveButton onClick={() => onSecChange("prev")}>
                            <BsFillCaretLeftFill />
                            <span>10초</span>
                        </view.MoveButton>
                    </view.MoveButtonWrapper>
                    <view.MoveButtonWrapper
                        move={"next"}
                        onMouseEnter={(e) => {
                            e.currentTarget.classList.add("hover");
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.classList.remove("hover");
                        }}>
                        <view.MoveButton onClick={() => onSecChange("next")}>
                            <BsFillCaretRightFill />
                            <span>10초</span>
                        </view.MoveButton>
                    </view.MoveButtonWrapper>
                </>
            ) : null}
            {is_buffer && (
                <view.BufferWrapper>
                    <h4>
                        <img
                            src="https://s3.ap-northeast-2.amazonaws.com/dev-img.bear-u.com/static/svg/viewer/warning.svg"
                            alt="영상 재생 문제 해결"
                        />
                        영상 재생에 문제가 있으신가요?
                    </h4>
                    <Link to={"/service/info/viewer"} target={"/_blank"} className={"solution-btn"}>
                        영상 문제 해결 방법 보기
                    </Link>
                </view.BufferWrapper>
            )}
            {is_error && (
                <view.ErrorWrapper>
                    {is_mobile ? <h3>문제가 발생했습니다 😰</h3> : <h2>문제가 발생했습니다 😰</h2>}
                    <div>해당 강의는 개인정보보호등의 사유로 인하여 재생이 어렵습니다.</div>
                    <div>문제가 지속될 경우 관리자에게 문의바랍니다</div>
                </view.ErrorWrapper>
            )}
            {can_render && (
                <ReactPlayer
                    ref={player}
                    playing={auto_play}
                    controls={true}
                    width={"100%"}
                    height={"100%"}
                    playsinline={true}
                    onBuffer={onBuffered}
                    onBufferEnd={onBufferedEnd}
                    onEnded={onEnded}
                    style={{
                        position: "absolute",
                        zIndex: "100",
                    }}
                    onStart={() => {
                        setIsPlay(true);
                        setIsBuffer(false);
                        setIsError(false);
                    }}
                    onPlay={onPlay}
                    onPause={onPause}
                    onReady={() => {
                        setIsBuffer(false);
                        setIsError(false);
                    }}
                    onError={onError}
                    config={{
                        vimeo: {
                            playerOptions: {
                                autopause: false,
                                autoplay: auto_play,
                                responsive: true,
                                height: "100%",
                                width: "100%",
                            },
                        },
                    }}
                    url={`https://vimeo.com/${subject_vimeo_id && subject_vimeo_id}`}
                />
            )}
            )}
        </>
    );
};

export default Player;

export const useComponentStyle = (is_mobile) => {
    const theme = useTheme();
    return {
        view: {
            MoveButtonWrapper: styled.div((props) => ({
                width: "10%",
                height: "17%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                boxSizing: "border-box",
                position: "absolute",
                zIndex: "5001",
                top: "35%",
                left: props.move === "prev" ? "5%" : "",
                right: props.move === "next" ? "5%" : "",
                opacity: "0",
                transition: "opacity .2s",
                "&.hover": {
                    opacity: ".7",
                },
            })),
            MoveButton: styled.div({
                width: "100%",
                height: "100%",
                borderRadius: "15px",
                zIndex: "5000",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                backgroundColor: theme.colors.dg1,
                cursor: "pointer",
                color: theme.colors.w,
                svg: {
                    fontSize: theme.font_size.f18,
                    height: "30%",
                    width: "30%",
                },
                span: {
                    position: "absolute",
                    bottom: "10%",
                    fontSize: theme.font_size.f14,
                },
                transition: "opacity .2s",
                "&:active": {
                    opacity: ".1",
                },
            }),
            BufferWrapper: styled.div({
                border: "1px solid white",
                position: "absolute",
                width: "50%",
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                borderRadius: "5px",
                boxShadow: "0 0 15px rgba(0, 0, 0, 0.3)",
                left: 0,
                right: 0,
                bottom: "14%",
                margin: "auto",
                padding: "1.5em 0",
                zIndex: "5002",
                textAlign: "center",
                transition: "all .2s",
                "@media (max-width: 620px)": {
                    width: "70%",
                },
                "@media (max-width: 320px)": {
                    width: "80%",
                },
                h4: {
                    fontSize: theme.font_size.f19,
                    fontWeight: "700",
                    color: theme.colors.w,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all .2s",
                    "@media (max-width: 450px)": {
                        fontSize: theme.font_size.f15,
                        lineHeight: theme.font_size.f15,
                        img: {
                            width: "1.1em",
                        },
                    },
                },
                "&:after": {
                    content: '""',
                    display: "block",
                    width: "40px",
                    height: "3px",
                    backgroundColor: "#aaaaaa",
                    position: "absolute",
                    left: "0",
                    right: "0",
                    margin: "auto",
                    bottom: "-20px",
                },
                "& > .solution-btn": {
                    width: "50%",
                    margin: "2em auto 0",
                    display: "block",
                    fontSize: theme.font_size.f16,
                    fontWeight: "400",
                    lineHeight: theme.font_size.f16,
                    color: theme.colors.w,
                    transition: "all .2s",
                    "@media (max-width: 450px)": {
                        fontSize: theme.font_size.f12,
                        lineHeight: theme.font_size.f12,
                    },
                },
            }),
            ErrorWrapper: styled.div({
                position: "absolute",
                width: "100%",
                height: "100%",
                backgroundColor: "black",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                zIndex: "5001",
                color: theme.colors.w,
                div: {
                    fontSize: is_mobile && theme.font_size.f14,
                    color: "white",
                    transition: "all .2s",
                    "@media (max-width: 325px)": {
                        fontSize: theme.font_size.f12,
                    },
                },
            }),
        },
    };
};
